import { ArrowUp } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from '@/hooks/use-translation'

type Props = {
  onSend: (text: string) => void
  disabled?: boolean
  initialText?: string
}

export function AgentInputBar({ onSend, disabled = false, initialText }: Props) {
  const t = useTranslation()
  const [text, setText] = useState(initialText ?? '')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const consumedInitialRef = useRef(false)

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = '24px'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }, [])

  // Populate the input once with any incoming prefill (e.g. from a shortcut
  // like "Publicar con Sergeo"). We do not auto-submit — the user reviews
  // and confirms manually.
  useEffect(() => {
    if (!initialText || consumedInitialRef.current) return
    consumedInitialRef.current = true
    setText(initialText)
    requestAnimationFrame(() => {
      adjustHeight()
      textareaRef.current?.focus()
    })
  }, [initialText, adjustHeight])

  const handleSend = useCallback(() => {
    const trimmed = text.trim()
    if (!trimmed || disabled) return
    onSend(trimmed)
    setText('')
    if (textareaRef.current) {
      textareaRef.current.style.height = '24px'
    }
  }, [text, disabled, onSend])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  const canSend = text.trim().length > 0 && !disabled

  return (
    <div className="border-t border-border px-4 py-3">
      <div className="flex items-end gap-2 bg-muted/50 rounded-xl px-3 py-2 border border-border">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value)
            adjustHeight()
          }}
          onKeyDown={handleKeyDown}
          placeholder={t('agent.chat.placeholder')}
          disabled={disabled}
          rows={1}
          className="flex-1 bg-transparent text-card-foreground placeholder:text-muted-foreground text-sm resize-none outline-none border-0 focus:!border-0 focus:!shadow-none min-h-[24px] max-h-[120px]"
          style={{ height: '24px' }}
        />
        <button
          onClick={handleSend}
          disabled={!canSend}
          className={`shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors duration-150 ${
            canSend
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'bg-muted text-muted-foreground'
          }`}
          aria-label="Send message"
        >
          <ArrowUp className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
